import {
    createContext,
    PropsWithChildren,
    startTransition,
    useCallback,
    useMemo,
    useState,
} from "react";
import { UrlUpdateType } from "use-query-params";
import {
    InvoiceByCounterpartySortValue,
    InvoicesByCounterpartyItem,
} from "../../common/types/invoice";
import { useInvoicesView } from "../../contexts/InvoicesViewContext";
import { useInvoicesByCounterparty } from "../../api/invoice.api";
import { useInvoicesByCounterpartyPageSortingCache } from "../../hooks/useInvoicesByCounterpartyPageSortingCache";
import {
    SortByCounterparty,
    useSortByCounterparty,
} from "./useSortByCounterparty";

const PER_PAGE = 50;

export interface InvoicesByCounterpartyTableContextValue {
    currentPage: number;
    totalPages: number;
    counterpartiesCount: number;
    setPage:
        | React.Dispatch<React.SetStateAction<number>>
        | ((
              newValue: number | null | undefined,
              updateType?: UrlUpdateType,
          ) => void);
    handleSortChange: SortByCounterparty["handleSortChange"];
    currentSort: SortByCounterparty["currentSort"];
    isLoading: boolean;
    data?: InvoicesByCounterpartyItem[];
}

const defaultValue: InvoicesByCounterpartyTableContextValue = {
    currentPage: 0,
    totalPages: 0,
    counterpartiesCount: 0,
    setPage: () => {},
    handleSortChange: () => {},
    currentSort: InvoiceByCounterpartySortValue.INVOICES_COUNT_DESC,
    isLoading: false,
};

export const InvoicesByCounterpartyTableContext =
    createContext<InvoicesByCounterpartyTableContextValue>(defaultValue);

export const InvoicesByCounterpartyTableContextProvider: React.FC<
    PropsWithChildren
> = ({ children }) => {
    const [cachedSorting, setCachedSorting] =
        useInvoicesByCounterpartyPageSortingCache();
    const { filters, viewOption } = useInvoicesView();
    const [page, setPage] = useState(1);

    const { sortExpression, currentSort, handleSortChange } =
        useSortByCounterparty(cachedSorting);

    const { data, isLoading } = useInvoicesByCounterparty(
        {
            filters,
            sort: sortExpression,
            page,
            limit: PER_PAGE,
        },
        { enabled: viewOption.groupBy === "counterparty" },
    );

    const handleSortChangeWithPageReset = useCallback(
        (value: InvoiceByCounterpartySortValue) => {
            startTransition(() => {
                handleSortChange(value);
                setCachedSorting(value);
                setPage(1);
            });
        },
        [handleSortChange, setCachedSorting, setPage],
    );

    const contextValue = useMemo<InvoicesByCounterpartyTableContextValue>(
        () => ({
            currentPage: page,
            totalPages: data?.pageCount ?? 1,
            counterpartiesCount: data?.total ?? 0,
            setPage,
            handleSortChange: handleSortChangeWithPageReset,
            currentSort,
            isLoading,
            data: data?.data,
        }),
        [page, data, handleSortChangeWithPageReset, currentSort, isLoading],
    );

    return (
        <InvoicesByCounterpartyTableContext.Provider value={contextValue}>
            {children}
        </InvoicesByCounterpartyTableContext.Provider>
    );
};
